/* SansMinds — archive entry tutorial player.
   The video is gated SERVER-SIDE in Liquid: under-tier members never receive
   the embed URL at all. For authorized members this is a click-to-load facade
   that swaps the poster for the Vimeo player, then saves watch progress via the
   App Proxy /progress endpoint (stored in the app's Postgres — NO Vimeo API
   token, NO R2). Chapter rows are clickable and seek the player to their
   timestamp. Gated files are plain links, rendered only for authorized members. */
(function () {
  'use strict';

  /* Vimeo Player SDK — client-side library (not the API token). */
  var vimeoSDK;
  function loadVimeoSDK() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
    if (vimeoSDK) return vimeoSDK;
    vimeoSDK = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://player.vimeo.com/api/player.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return vimeoSDK;
  }

  /* Each play button (trailer, tutorial, …) loads independently. Its load
     promise is stashed on the button so re-clicking is a no-op WITHOUT blocking
     the other players on the page. `tutorialPlayer` is the single gated player
     that chapter seeks + progress tracking target. */
  var loads = new WeakMap(); // btn -> Promise<Vimeo.Player>
  var tutorialPlayer = null; // Vimeo.Player instance for the tutorial (data-player)

  function attachProgress(p, entry, proxyBase, title, url) {
    if (!entry) return;
    var lastSent = 0;
    var lastPct = -1;
    function save(pct) {
      fetch(proxyBase + '/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ entryId: entry, pct: pct, lastSeen: new Date().toISOString(), title: title, url: url }),
      }).catch(function () {});
    }
    p.on('timeupdate', function (d) {
      var pct = Math.round((d.percent || 0) * 100);
      var now = Date.now();
      if (pct !== lastPct && now - lastSent > 15000) {
        lastSent = now;
        lastPct = pct;
        save(pct);
      }
    });
    p.on('ended', function () { save(100); });
  }

  /* Visible deterrent overlay (member label). Removable — NOT forensic. */
  function addOverlay(container, label) {
    if (!container || !label || container.querySelector('.ae-player__wm')) return;
    var wm = document.createElement('span');
    wm.className = 'ae-player__wm';
    wm.textContent = label;
    container.appendChild(wm);
  }

  /* Build a sorted [{el, sec}] list of the chapter seek-buttons. */
  function buildChapters(playerEl) {
    var scope = (playerEl && playerEl.closest('.archive-entry')) || document;
    var btns = scope.querySelectorAll('[data-seek-ts]');
    var list = [];
    for (var i = 0; i < btns.length; i++) {
      var sec = parseTs(btns[i].getAttribute('data-seek-ts'));
      if (sec != null) list.push({ el: btns[i], sec: sec });
    }
    list.sort(function (a, b) { return a.sec - b.sec; });
    return list;
  }

  /* Mark the chapter the playhead is currently in. */
  function highlightActive(chapters, currentSec) {
    var activeIdx = -1;
    for (var i = 0; i < chapters.length; i++) {
      if (chapters[i].sec <= currentSec + 0.25) activeIdx = i; else break;
    }
    for (var j = 0; j < chapters.length; j++) {
      var on = j === activeIdx;
      chapters[j].el.classList.toggle('is-active', on);
      if (on) chapters[j].el.setAttribute('aria-current', 'true');
      else chapters[j].el.removeAttribute('aria-current');
    }
  }

  /* Swap the poster for the Vimeo iframe (once) and return a promise to the player. */
  function loadPlayer(btn) {
    var existing = loads.get(btn);
    if (existing) return existing;
    var embed = btn.getAttribute('data-embed');
    if (!embed) return Promise.reject(new Error('no embed'));

    var playerEl = btn.closest('[data-player]');
    var poster = btn.closest('[data-player-poster]') || btn.parentNode;
    var entry = playerEl && playerEl.getAttribute('data-entry');
    var proxyBase = (playerEl && playerEl.getAttribute('data-proxy')) || '/apps/sansminds';
    var wmLabel = playerEl && playerEl.getAttribute('data-wm');
    var entryTitle = playerEl && playerEl.getAttribute('data-title');
    var entryUrl = playerEl && playerEl.getAttribute('data-url');

    var sep = embed.indexOf('?') === -1 ? '?' : '&';
    var frame = document.createElement('iframe');
    frame.className = 'ae-player__iframe';
    frame.src = embed + sep + 'autoplay=1';
    frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('title', 'Tutorial video');
    poster.replaceWith(frame);
    if (wmLabel && playerEl) addOverlay(playerEl, 'SansMinds · ' + wmLabel);

    var promise = loadVimeoSDK().then(function () {
      var p = new window.Vimeo.Player(frame);
      // Progress + chapters only apply to the gated tutorial player, which lives
      // inside [data-player]. The public trailer has no such wrapper.
      if (playerEl) {
        tutorialPlayer = p;
        attachProgress(p, entry, proxyBase, entryTitle, entryUrl);
        var chapters = buildChapters(playerEl);
        if (chapters.length) {
          p.on('timeupdate', function (d) { highlightActive(chapters, d.seconds || 0); });
        }
      }
      return p;
    });
    loads.set(btn, promise);
    return promise;
  }

  /* Seek the tutorial to `seconds`, loading the player first if needed. */
  function seekTo(seconds) {
    function go(p) {
      p.setCurrentTime(seconds).then(function () { p.play().catch(function () {}); }).catch(function () {});
    }
    if (tutorialPlayer) { go(tutorialPlayer); return; }
    var btn = document.querySelector('[data-player] [data-play-video]');
    if (btn) loadPlayer(btn).then(go).catch(function () {});
  }

  /* Parse "M:SS", "MM:SS", "HH:MM:SS", a bare number, or a range
     ("00:00 - 01:22" → start) into seconds. */
  function parseTs(raw) {
    if (!raw) return null;
    var s = String(raw).split(/[-–—]/)[0].trim();
    var parts = s.split(':');
    if (!parts.length || parts.length > 3) return null;
    var sec = 0;
    for (var i = 0; i < parts.length; i++) {
      var n = parseInt(parts[i], 10);
      if (isNaN(n)) return null;
      sec = sec * 60 + n;
    }
    return sec;
  }

  document.addEventListener('click', function (e) {
    var v = e.target.closest('[data-play-video]');
    if (v) { e.preventDefault(); loadPlayer(v).catch(function () {}); return; }

    var ch = e.target.closest('[data-seek-ts]');
    if (ch) {
      var sec = parseTs(ch.getAttribute('data-seek-ts'));
      if (sec != null) { e.preventDefault(); seekTo(sec); }
    }
  });
})();
